<template>
  <Dialog
    :visible="visible"
    modal
    append-to="body"
    :closable="false"
    :draggable="false"
    class="project-settings-dialog w-full"
    :pt="dialogPt"
    data-testid="project-settings-dialog"
    @update:visible="onVisibleUpdate"
  >
    <template #header>
      <div class="project-settings-dialog__header">
        <div class="project-settings-dialog__header-top">
          <div class="project-settings-dialog__header-text">
            <h2 id="project-settings-dialog-title" class="project-settings-dialog__title">
              {{ $t('UserProjects.settings.title') }}
            </h2>
            <p class="project-settings-dialog__subtitle">
              {{ projectName }}
            </p>
          </div>
          <button
            type="button"
            class="project-settings-dialog__close"
            :disabled="saving"
            :aria-label="$t('UserProjects.settings.cancel')"
            data-testid="project-settings-close"
            @click="close"
          >
            <i class="pi pi-times" aria-hidden="true" />
          </button>
        </div>

        <div class="project-settings-dialog__tabs" role="tablist">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            type="button"
            role="tab"
            class="project-settings-dialog__tab"
            :class="{ 'project-settings-dialog__tab--active': activeTab === tab.id }"
            :aria-selected="activeTab === tab.id"
            :data-testid="`project-settings-tab-${tab.id}`"
            @click="activeTab = tab.id"
          >
            {{ tab.label }}
          </button>
        </div>
      </div>
    </template>

    <div class="project-settings-dialog__body">
      <div v-if="loading" class="project-settings-dialog__loading">
        <i class="pi pi-spin pi-spinner mr-2" />
        {{ $t('UserProjects.settings.loading') }}
      </div>

      <Message v-else-if="error" severity="error" :closable="false" class="mb-3">
        {{ error }}
      </Message>

      <template v-else>
        <ProjectSettingsGeneralTab
          v-if="activeTab === 'general'"
          v-model="form"
          :current-ppc="settings?.currentPpc"
          :ppc-gap="settings?.ppcGap"
          :can-manage="canManage"
          @request-closure="openClosureWizard"
        />
        <ProjectSettingsTeamTab
          v-else-if="activeTab === 'team'"
          :members="members"
          :can-manage="canManage"
          :linked-teams="linkedTeamLabels"
          :users="users"
          :busy="membersBusy"
          @add="onAddMembers"
          @remove="onRemoveMember"
          @change-role="onChangeRole"
        />
        <ProjectSettingsNotificationsTab
          v-else-if="activeTab === 'notifications'"
          v-model="form.notifications"
          :can-manage="canManage"
        />
      </template>
    </div>

    <template #footer>
      <div class="project-settings-dialog__footer">
        <button
          type="button"
          class="project-settings-dialog__btn-cancel"
          :disabled="saving"
          data-testid="project-settings-cancel"
          @click="close"
        >
          {{ $t('UserProjects.settings.cancel') }}
        </button>
        <button
          v-if="canManage"
          type="button"
          class="project-settings-dialog__btn-save"
          :disabled="saving"
          :aria-busy="saving"
          data-testid="project-settings-save"
          @click="save"
        >
          <i v-if="saving" class="pi pi-spin pi-spinner mr-2" aria-hidden="true" />
          {{ $t('UserProjects.settings.save') }}
        </button>
      </div>
    </template>
  </Dialog>

  <ProjectClosureRequestWizard
    v-if="closureVisible"
    :visible="closureVisible"
    :project-id="projectId"
    :project-name="projectName"
    @update:visible="closureVisible = $event"
    @requested="onClosureRequested"
  />
</template>

<script>
import Dialog from 'primevue/dialog';
import Message from 'primevue/message';
import { useToast } from 'primevue/usetoast';
import pmProjectSettingsService from '@/services/pmProjectSettingsService';
import ProjectSettingsGeneralTab from './ProjectSettingsGeneralTab.vue';
import ProjectSettingsTeamTab from './ProjectSettingsTeamTab.vue';
import ProjectSettingsNotificationsTab from './ProjectSettingsNotificationsTab.vue';
import ProjectClosureRequestWizard from './ProjectClosureRequestWizard.vue';

const EMPTY_NOTIFICATIONS = () => ({
  ppc_threshold: true,
  commitment_deadline: true,
  user_inactive: true,
  tbi_threshold: true,
  weekly_reminder: true,
  behavior_pattern: true,
  behaviorPatternThreshold: 3,
  behaviorPatternWeeks: 4,
});

export default {
  name: 'ProjectSettingsDialog',
  components: {
    Dialog,
    Message,
    ProjectSettingsGeneralTab,
    ProjectSettingsTeamTab,
    ProjectSettingsNotificationsTab,
    ProjectClosureRequestWizard,
  },
  props: {
    visible: { type: Boolean, default: false },
    projectId: { type: String, default: '' },
    projectName: { type: String, default: '' },
    users: { type: Array, default: () => [] },
    teamLookup: { type: Object, default: () => ({}) },
  },
  emits: ['update:visible', 'saved', 'closed-requested'],
  setup() {
    return { toast: useToast() };
  },
  data() {
    return {
      activeTab: 'general',
      loading: false,
      saving: false,
      error: '',
      settings: null,
      members: [],
      membersBusy: false,
      canManage: false,
      form: {
        name: '',
        description: '',
        startDate: '',
        ppcTargetPercent: 80,
        commitmentCloseWeekday: 4,
        notifications: EMPTY_NOTIFICATIONS(),
      },
      closureVisible: false,
    };
  },
  computed: {
    tabs() {
      return [
        { id: 'general', label: this.$t('UserProjects.settings.tabs.general') },
        { id: 'team', label: this.$t('UserProjects.settings.tabs.team') },
        { id: 'notifications', label: this.$t('UserProjects.settings.tabs.notifications') },
      ];
    },
    linkedTeamLabels() {
      const ids = this.settings?.teams || [];
      return ids.map((id) => this.teamLookup[String(id)] || id);
    },
    dialogPt() {
      return {
        root: {
          'data-testid': 'project-settings-dialog',
          'aria-labelledby': 'project-settings-dialog-title',
        },
      };
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(next) {
        if (next && this.projectId) {
          this.load();
        }
      },
    },
  },
  methods: {
    onVisibleUpdate(value) {
      this.$emit('update:visible', value);
    },
    close() {
      this.$emit('update:visible', false);
    },
    toDateInput(value) {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      return date.toISOString().slice(0, 10);
    },
    async load() {
      this.loading = true;
      this.error = '';
      this.activeTab = 'general';
      try {
        const [settings, members] = await Promise.all([
          pmProjectSettingsService.getSettings(this.projectId),
          pmProjectSettingsService.listMembers(this.projectId),
        ]);
        this.settings = settings;
        this.canManage = !!settings.canManage;
        this.form = {
          name: settings.name || '',
          description: settings.description || '',
          startDate: this.toDateInput(settings.startDate),
          ppcTargetPercent: settings.settings?.ppcTargetPercent ?? 80,
          commitmentCloseWeekday: settings.settings?.commitmentCloseWeekday ?? 4,
          notifications: {
            ...EMPTY_NOTIFICATIONS(),
            ...(settings.settings?.notifications || {}),
          },
        };
        this.members = members.items || [];
      } catch (error) {
        this.error = error?.response?.data?.message
          || this.$t('UserProjects.settings.error');
      } finally {
        this.loading = false;
      }
    },
    async save() {
      if (!this.canManage) return;
      this.saving = true;
      try {
        await pmProjectSettingsService.updateSettings(this.projectId, {
          name: this.form.name.trim(),
          description: this.form.description,
          startDate: this.form.startDate
            ? new Date(this.form.startDate).toISOString()
            : undefined,
          ppcTargetPercent: this.form.ppcTargetPercent,
          commitmentCloseWeekday: this.form.commitmentCloseWeekday,
          notifications: this.form.notifications,
        });
        this.toast.add({
          severity: 'success',
          summary: this.$t('UserProjects.settings.toastSaved'),
          life: 3000,
        });
        this.$emit('saved');
        this.close();
      } catch (error) {
        this.toast.add({
          severity: 'error',
          summary: error?.response?.data?.message
            || this.$t('UserProjects.settings.error'),
          life: 4000,
        });
      } finally {
        this.saving = false;
      }
    },
    async onAddMembers(payload) {
      const userIds = Array.isArray(payload) ? payload : (payload?.userIds || []);
      const role = Array.isArray(payload) ? 'member' : (payload?.role || 'member');
      if (!userIds.length) return;

      this.membersBusy = true;
      try {
        let data = await pmProjectSettingsService.addMembers(this.projectId, userIds);
        this.members = data.items || [];
        if (role && role !== 'member') {
          for (const userId of userIds) {
            data = await pmProjectSettingsService.updateMemberRole(
              this.projectId,
              userId,
              role,
            );
          }
          this.members = data.items || [];
        }
      } catch (error) {
        this.toast.add({
          severity: 'error',
          summary: error?.response?.data?.message
            || this.$t('UserProjects.settings.error'),
          life: 4000,
        });
      } finally {
        this.membersBusy = false;
      }
    },
    async onRemoveMember(userId) {
      this.membersBusy = true;
      try {
        const data = await pmProjectSettingsService.removeMember(this.projectId, userId);
        this.members = data.items || [];
      } catch (error) {
        this.toast.add({
          severity: 'error',
          summary: error?.response?.data?.message
            || this.$t('UserProjects.settings.error'),
          life: 4000,
        });
      } finally {
        this.membersBusy = false;
      }
    },
    async onChangeRole({ userId, role }) {
      this.membersBusy = true;
      try {
        const data = await pmProjectSettingsService.updateMemberRole(
          this.projectId,
          userId,
          role,
        );
        this.members = data.items || [];
      } catch (error) {
        this.toast.add({
          severity: 'error',
          summary: error?.response?.data?.message
            || this.$t('UserProjects.settings.error'),
          life: 4000,
        });
      } finally {
        this.membersBusy = false;
      }
    },
    openClosureWizard() {
      this.closureVisible = true;
    },
    onClosureRequested() {
      this.closureVisible = false;
      this.$emit('closed-requested');
      this.close();
    },
  },
};
</script>

<style scoped>
.project-settings-dialog__header {
  width: 100%;
}

.project-settings-dialog__header-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.25rem;
}

.project-settings-dialog__header-text {
  min-width: 0;
}

.project-settings-dialog__title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--color-dark, #1a1a1a);
}

.project-settings-dialog__subtitle {
  margin: 0.125rem 0 0;
  font-size: 13px;
  line-height: 1.4;
  color: #6b6b6b;
}

.project-settings-dialog__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: none;
  border-radius: 0.5rem;
  background: transparent;
  color: var(--color-disabled, #6b6b6b);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.project-settings-dialog__close:hover:not(:disabled) {
  background: var(--color-surface-hover, #f5f5f5);
  color: var(--color-dark, #1a1a1a);
}

.project-settings-dialog__close:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.project-settings-dialog__tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 1rem;
}

.project-settings-dialog__tab {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: #6b6b6b;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.project-settings-dialog__tab:hover {
  background: var(--color-surface-hover, #f5f5f5);
}

.project-settings-dialog__tab--active {
  background: #fdf0e6;
  color: var(--color-orange, #e4821a);
}

.project-settings-dialog__body {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.project-settings-dialog__loading {
  padding: 2.5rem 0;
  text-align: center;
  color: var(--color-disabled, #6b6b6b);
}

.project-settings-dialog__footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  width: 100%;
}

.project-settings-dialog__btn-cancel {
  padding: 0.5rem 1rem;
  border: 1px solid var(--border-color, #e5e5e5);
  border-radius: 10px;
  background: var(--color-white, #fff);
  color: var(--color-dark, #1a1a1a);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease;
}

.project-settings-dialog__btn-cancel:hover:not(:disabled) {
  background: var(--color-surface-hover, #f5f5f5);
}

.project-settings-dialog__btn-cancel:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.project-settings-dialog__btn-save {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 10px;
  background: var(--accent-gradient);
  color: var(--color-white, #fff);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.15s ease;
}

.project-settings-dialog__btn-save:hover:not(:disabled) {
  opacity: 0.92;
}

.project-settings-dialog__btn-save:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
</style>

<style>
.project-settings-dialog.p-dialog {
  display: flex;
  flex-direction: column;
  width: min(600px, calc(100vw - 2rem));
  max-height: 90vh;
  margin: 1rem;
  border: none;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  font-family: var(--font-family, 'AsenPro', sans-serif);
}

.project-settings-dialog.p-dialog .p-dialog-header {
  flex-shrink: 0;
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid var(--border-color, #e5e5e5);
  background: var(--color-white);
}

.project-settings-dialog.p-dialog .p-dialog-content {
  flex: 1 1 auto;
  min-height: 0;
  padding: 1.25rem 1.5rem;
  background: var(--color-white);
  overflow-x: hidden;
  overflow-y: auto;
}

.project-settings-dialog.p-dialog .p-dialog-footer {
  flex-shrink: 0;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border-color, #e5e5e5);
  background: var(--color-white);
}

.project-settings-dialog .project-settings-field.p-inputtext,
.project-settings-dialog .project-settings-field.p-textarea,
.project-settings-dialog .project-settings-field.p-select {
  width: 100%;
  border: 1.5px solid #e5e5e5;
  border-radius: 10px;
  background: var(--color-white);
  font-size: 14px;
  color: var(--color-dark, #1a1a1a);
  box-shadow: none;
}

.project-settings-dialog .project-settings-field.p-inputtext {
  height: 40px;
  padding: 0 0.75rem;
}

.project-settings-dialog .project-settings-field.p-textarea {
  padding: 0.625rem 0.75rem;
  resize: none;
}

.project-settings-dialog .project-settings-field.p-select {
  height: 40px;
}

.project-settings-dialog .project-settings-field.p-select .p-select-label {
  padding: 0.5rem 0.75rem;
  font-size: 14px;
}

.project-settings-dialog .project-settings-field.p-inputtext:enabled:focus,
.project-settings-dialog .project-settings-field.p-textarea:enabled:focus,
.project-settings-dialog .project-settings-field.p-select.p-focus {
  border-color: var(--color-orange, #e4821a);
  box-shadow: 0 0 0 2px rgba(228, 130, 26, 0.2);
  outline: none;
}

.project-settings-dialog .project-settings-field--sm.p-inputtext {
  width: 3.5rem;
  height: 32px;
  padding: 0 0.5rem;
  border-radius: 8px;
  font-size: 13px;
  text-align: center;
}

.project-settings-dialog .project-settings-field--ppc.p-inputtext {
  width: 4rem;
  height: 32px;
  padding: 0 0.5rem;
  border-radius: 8px;
  font-size: 13px;
  text-align: center;
}
</style>
